<template>
  <teleport to="body">
    <transition name="app-loader-fade" @after-leave="onAfterLeave">
      <div
        v-if="isAppLoading"
        class="app-loader-overlay"
        role="status"
        aria-live="assertive"
        aria-label="Application loading"
      >
        <!-- Glass card container -->
        <div class="app-loader-card">
          <!-- Large premium spinner -->
          <div class="app-loader-spinner" aria-hidden="true">
            <div class="spinner-ring spinner-ring--outer" />
            <div class="spinner-ring spinner-ring--inner" />
            <div class="spinner-dot" />
          </div>

          <!-- Wordmark -->
          <div class="app-loader-wordmark" aria-hidden="true">
            <span class="wordmark-core">COREVIAN</span>
            <span class="wordmark-tag">ERP</span>
          </div>

          <!-- Contextual message -->
          <p class="app-loader-message">{{ message }}</p>

          <!-- Pulse progress track -->
          <div class="app-loader-pulse" aria-hidden="true">
            <div class="pulse-track">
              <div class="pulse-fill" />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
const MESSAGES = [
  'Loading workspace...',
  'Fetching your data...',
  'Preparing dashboard...',
  'Initializing modules...',
  'Establishing connection...',
];

let messageIndex = 0;

export default {
  name: 'AppLoader',

  data() {
    return {
      message: MESSAGES[0],
    };
  },

  computed: {
    isAppLoading() {
      return this.$store.getters['loading/isAppLoading'];
    },
    storeMessage() {
      return this.$store.getters['loading/appLoaderMessage'];
    },
  },

  watch: {
    isAppLoading(newVal) {
      if (newVal) {
        this.message = this.storeMessage || MESSAGES[messageIndex % MESSAGES.length];
        messageIndex++;
      }
    },
  },

  methods: {
    onAfterLeave() {},
  },
};
</script>

<style scoped>
/* ── Backdrop Overlay ─────────────────────────────────────────────────────── */
.app-loader-overlay {
  position: fixed;
  inset: 0;
  z-index: calc(var(--z-toast, 9999) + 1);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Blurred semi-transparent dark backdrop */
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

/* ── Glass Card ───────────────────────────────────────────────────────────── */
.app-loader-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  background: rgba(255, 255, 255, 0.97);
  border-radius: 20px;
  padding: 40px 48px;
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.18),
    0 4px 16px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(226, 232, 240, 0.6);
  min-width: 260px;
}

/* ── Spinner ──────────────────────────────────────────────────────────────── */
.app-loader-spinner {
  position: relative;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner-ring {
  position: absolute;
  border-radius: 50%;
  border-style: solid;
  border-color: transparent;
}

.spinner-ring--outer {
  width: 64px;
  height: 64px;
  border-width: 3px;
  border-top-color: var(--primary, #0085ff);
  border-right-color: rgba(0, 133, 255, 0.25);
  animation: spinner-rotate 0.85s cubic-bezier(0.6, 0, 0.4, 1) infinite;
}

.spinner-ring--inner {
  width: 46px;
  height: 46px;
  border-width: 2.5px;
  border-bottom-color: var(--primary, #0085ff);
  border-left-color: rgba(0, 133, 255, 0.15);
  animation: spinner-rotate 1.1s cubic-bezier(0.6, 0, 0.4, 1) infinite reverse;
}

.spinner-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--primary, #0085ff);
  animation: dot-pulse 1.6s ease-in-out infinite;
}

/* ── Wordmark ─────────────────────────────────────────────────────────────── */
.app-loader-wordmark {
  display: flex;
  align-items: baseline;
  gap: 6px;
  letter-spacing: 0.12em;
}

.wordmark-core {
  font-family: var(--font-display, 'Outfit', sans-serif);
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--slate-900, #0f172a);
  letter-spacing: 0.12em;
}

.wordmark-tag {
  font-family: var(--font-body, 'Inter', sans-serif);
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: var(--primary, #0085ff);
  background: #eff6ff;
  padding: 2px 6px;
  border-radius: 4px;
  vertical-align: middle;
}

/* ── Message ──────────────────────────────────────────────────────────────── */
.app-loader-message {
  font-family: var(--font-body, 'Inter', sans-serif);
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--slate-500, #64748b);
  letter-spacing: 0.01em;
  margin: 0;
  text-align: center;
}

/* ── Pulse Track ──────────────────────────────────────────────────────────── */
.pulse-track {
  width: 140px;
  height: 2px;
  background: var(--slate-100, #f1f5f9);
  border-radius: 99px;
  overflow: hidden;
}

.pulse-fill {
  height: 100%;
  width: 40%;
  background: linear-gradient(90deg, transparent, var(--primary, #0085ff), transparent);
  border-radius: 99px;
  animation: pulse-sweep 1.6s ease-in-out infinite;
}

/* ── Transitions ──────────────────────────────────────────────────────────── */
.app-loader-fade-enter-active {
  transition: opacity 0.2s ease;
}
.app-loader-fade-leave-active {
  transition: opacity 0.28s ease;
}
.app-loader-fade-enter-from,
.app-loader-fade-leave-to {
  opacity: 0;
}

/* Card scale in */
.app-loader-fade-enter-active .app-loader-card {
  transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.2s ease;
}
.app-loader-fade-leave-active .app-loader-card {
  transition: transform 0.2s ease, opacity 0.28s ease;
}
.app-loader-fade-enter-from .app-loader-card,
.app-loader-fade-leave-to .app-loader-card {
  transform: scale(0.94);
  opacity: 0;
}

/* ── Keyframes ────────────────────────────────────────────────────────────── */
@keyframes spinner-rotate {
  to { transform: rotate(360deg); }
}

@keyframes dot-pulse {
  0%, 100% { transform: scale(1);    opacity: 1;   }
  50%       { transform: scale(0.6); opacity: 0.4; }
}

@keyframes pulse-sweep {
  0%   { transform: translateX(-200%); }
  100% { transform: translateX(500%);  }
}

/* ── Reduced Motion ───────────────────────────────────────────────────────── */
@media (prefers-reduced-motion: reduce) {
  .spinner-ring--outer,
  .spinner-ring--inner { animation: none; }
  .spinner-dot         { animation: none; }
  .pulse-fill          { animation: none; width: 100%; }
  .app-loader-fade-enter-active,
  .app-loader-fade-leave-active { transition: none; }
}
</style>
